import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { 
  Plus, 
  Undo
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuotationForm } from '@/hooks/useQuotationForm';
import { QuotationBasicInfoForm } from '@/components/quotation/BasicInfoForm';
import { QuotationPageComponent } from '@/components/quotation/QuotationPage';
import { QuotationPage, QuotationBlock } from '@/components/quotation/types';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Sidebar } from '@/components/layout/Sidebar';

export default function QuotationForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { 
    quotation, 
    handleQuotationChange, 
    addPage, 
    updatePage, 
    deletePage, 
    duplicatePage,
    reorderPages, 
    addBlock, 
    updateBlock, 
    deleteBlock, 
    duplicateBlock,
    reorderBlocks,
    saveQuotation,
    isEditing,
    undo,
    canUndo,
    migrateOrphanPages,
    moveTourToPage,
    moveRestaurantToPage,
    copyPageToQuotation
  } = useQuotationForm();
  
  const [isSaving, setIsSaving] = useState(false);
  const [headerTitle, setHeaderTitle] = useState<string>(
    quotation.title || (isEditing ? 'Editar Cotação' : 'Nova Cotação')
  );
  
  // Estado para controlar a página ativa na sidebar
  const [activePage, setActivePage] = useState<string | null>(null);
  const [showBasicInfo, setShowBasicInfo] = useState(true);
  
  // Estados para controlar o diálogo de confirmação de saída
  const [showExitPrompt, setShowExitPrompt] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(quotation.updatedAt || null);
  
  // Referência para o conteúdo principal, para scroll
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Toggle page visibility function
  const togglePageVisibility = (pageId: string) => {
    const page = quotation.pages.find(p => p.id === pageId);
    if (page) {
      updatePage(pageId, 'isVisible', page.isVisible === false ? true : false);
    }
  };

  // Atualizar o título do header quando o título da cotação mudar
  useEffect(() => {
    if (quotation.title) {
      setHeaderTitle(quotation.title);
    } else {
      setHeaderTitle(isEditing ? 'Editar Cotação' : 'Nova Cotação');
    }
  }, [quotation.title, isEditing]);
  
  // Garantir que quando showBasicInfo é true, activePage é sempre null
  useEffect(() => {
    if (showBasicInfo && activePage !== null) {
      setActivePage(null);
    }
  }, [showBasicInfo]);
  
  // Atualizar a página ativa quando as páginas mudarem
  useEffect(() => {
    if (quotation.pages.length > 0 && !activePage && !showBasicInfo) {
      setActivePage(quotation.pages[0].id);
    } else if (quotation.pages.length === 0) {
      setActivePage(null);
      setShowBasicInfo(true);
    } else if (activePage && !quotation.pages.some(page => page.id === activePage)) {
      setActivePage(quotation.pages[0].id);
    }
  }, [quotation.pages, activePage, showBasicInfo]);

  // Detectar alterações não salvas
  useEffect(() => {
    setHasUnsavedChanges(quotation.updatedAt ? quotation.updatedAt > (lastSavedTime || new Date(0)) : false);
  }, [quotation, lastSavedTime]);

  // Improved beforeunload handling with user interaction detection
  useEffect(() => {
    // Don't do anything if there are no unsaved changes
    if (!hasUnsavedChanges) {
      return;
    }

    // Flag to track if user has interacted with the page
    let userHasInteracted = false;
    
    // Handler for user interaction events
    const handleUserInteraction = () => {
      userHasInteracted = true;
      
      // Once user has interacted, we can remove these listeners
      window.removeEventListener('mousedown', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
      window.removeEventListener('touchstart', handleUserInteraction);
      window.removeEventListener('scroll', handleUserInteraction);
      
      // And add the actual beforeunload listener
      window.addEventListener('beforeunload', handleBeforeUnload);
    };
    
    // The beforeunload handler - only added after user interaction
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    // Add listeners for common user interactions
    window.addEventListener('mousedown', handleUserInteraction);
    window.addEventListener('keydown', handleUserInteraction);
    window.addEventListener('touchstart', handleUserInteraction);
    window.addEventListener('scroll', handleUserInteraction);
    
    // Cleanup function
    return () => {
      // Clean up all event listeners
      window.removeEventListener('mousedown', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
      window.removeEventListener('touchstart', handleUserInteraction);
      window.removeEventListener('scroll', handleUserInteraction);
      
      // Only clean up beforeunload if it was added (after user interaction)
      if (userHasInteracted) {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    };
  }, [hasUnsavedChanges]);

  // Gerenciar navegação interna com alterações não salvas
  const handleInternalNavigation = useCallback((path: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowExitPrompt(true);
    } else {
      navigate(path);
    }
  }, [hasUnsavedChanges, navigate]);

  const confirmNavigation = () => {
    setShowExitPrompt(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const cancelNavigation = () => {
    setShowExitPrompt(false);
    setPendingNavigation(null);
  };

  // Formatação de data para o diálogo de confirmação
  const formatLastSavedTime = (date: Date | null) => {
    if (!date) return 'Nunca salvo';
    return format(date, "dd 'de' MMMM 'às' HH:mm:ss", { locale: ptBR });
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const { source, destination, type } = result;

    // Reordenar páginas
    if (type === 'PAGE') {
      reorderPages(source.index, destination.index);
      return;
    }

    // Reordenar blocos dentro de uma página
    if (type === 'BLOCK') {
      const pageId = source.droppableId.replace('page-', '').replace('-blocks', '');
      reorderBlocks(pageId, source.index, destination.index);
    }
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    setIsSaving(true);
    
    try {
      await saveQuotation();
      
      // Atualizar o último horário de salvamento
      setLastSavedTime(new Date());
      setHasUnsavedChanges(false);
      
      toast({
        title: "Cotação salva",
        description: "A cotação foi salva com sucesso."
      });
    } catch (error) {
      console.error('Error saving quotation:', error);
      
      toast({
        title: "Erro ao salvar",
        description: "Ocorreu um erro ao salvar a cotação.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
    
    return false; // Prevenir comportamento padrão do formulário
  };

  const handlePublish = () => {
    // O processo de publicação é gerenciado no Header
    // Este callback é chamado depois que a publicação é concluída
    toast({
      title: "Link pronto para compartilhar",
      description: "A cotação foi publicada e está pronta para ser compartilhada com o cliente."
    });
  };

  const handlePreview = () => {
    // Salvar antes de visualizar
    saveQuotation();
    // Atualizar o último horário de salvamento
    setLastSavedTime(new Date());
    setHasUnsavedChanges(false);
    // Abrir em uma nova aba sem o parâmetro from=editor
    window.open(`/quotation/${quotation.id}`, '_blank');
  };

  const handleBack = () => {
    handleInternalNavigation('/admin/travel-hub');
  };
  
  // Função para navegar para as informações básicas
  const handleShowBasicInfo = () => {
    setShowBasicInfo(true);
    setActivePage(null);
    
    // Scroll para o topo quando mostrar informações básicas
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo(0, 0);
    }
  };
  
  // Adicionar nova página e torná-la ativa
  const handleAddPage = () => {
    addPage();
    setShowBasicInfo(false);
    
    // A página recém-adicionada será mostrada através do useEffect quando as páginas forem atualizadas
  };

  // Lidar com o desfazer da última ação
  const handleUndo = () => {
    if (canUndo) {
      undo();
    }
  };

  return (
    <AdminLayout 
      title={headerTitle}
      isEditing={isEditing}
      onBack={handleBack}
      onPreview={handlePreview}
      onSubmit={handleSave}
      showActions={true}
      isSaving={isSaving}
      quotationPages={quotation.pages}
      activePage={activePage}
      setActivePage={(pageId) => {
        if (pageId) {
          setActivePage(pageId);
          setShowBasicInfo(false);
        } else {
          // Se pageId for null ou undefined, mostrar informações básicas
          setActivePage(null);
          setShowBasicInfo(true);
        }
      }}
      addPage={handleAddPage}
      removePage={(pageId) => {
        deletePage(pageId);
      }}
      reorderPages={reorderPages}
      duplicatePage={duplicatePage}
      onSelectQuotationBasicInfo={handleShowBasicInfo}
      canUndo={canUndo}
      onUndo={handleUndo}
      useDayMode={quotation.useDayMode}
      quotationId={quotation.id}
      onPublish={handlePublish}
      safeNavigate={handleInternalNavigation}
      togglePageVisibility={togglePageVisibility}
      copyPageToQuotation={copyPageToQuotation}
      currentQuotationId={quotation.id || ''}
    >
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="space-y-6" ref={mainContentRef}>
          {/* Informações Básicas */}
          {showBasicInfo && (
            <QuotationBasicInfoForm 
              quotation={quotation} 
              handleChange={handleQuotationChange} 
              isEditing={isEditing} 
              saveQuotation={saveQuotation}
              migrateOrphanPages={migrateOrphanPages}
            />
          )}
        
          {/* Conteúdo da Página Ativa */}
          {!showBasicInfo && activePage && (
            <div className="bg-white p-6">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  {quotation.pages.find(p => p.id === activePage)?.title || 'Página da Cotação'}
                </h2>
              </div>
              
              <Droppable droppableId="active-page" type="PAGE">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={cn(
                      "min-h-[200px]",
                      snapshot.isDraggingOver ? "bg-gray-50" : ""
                    )}
                  >
                    {quotation.pages
                      .filter(page => page.id === activePage)
                      .map((page, index) => (
                        <QuotationPageComponent
                          key={page.id}
                          page={page}
                          index={index}
                          updatePage={(id, field, value) => updatePage(id, field, value)}
                          deletePage={(id) => deletePage(id)}
                          addBlock={(pageId) => addBlock(pageId)}
                          updateBlock={(pageId, blockId, field, value) => updateBlock(pageId, blockId, field, value)}
                          deleteBlock={(pageId, blockId) => deleteBlock(pageId, blockId)}
                          duplicateBlock={(pageId, blockId) => duplicateBlock(pageId, blockId)}
                          allPages={quotation.pages}
                          moveTourToPage={(sourcePageId, tourId, targetPageId) => 
                            moveTourToPage(sourcePageId, tourId, targetPageId)
                          }
                          moveRestaurantToPage={(sourcePageId, restaurantId, targetPageId) => 
                            moveRestaurantToPage(sourcePageId, restaurantId, targetPageId)
                          }
                        />
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          )}
        </div>
      </DragDropContext>

      {/* Diálogo de confirmação ao sair sem salvar */}
      <AlertDialog open={showExitPrompt} onOpenChange={setShowExitPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sair sem salvar?</AlertDialogTitle>
            <AlertDialogDescription>
              Você tem alterações não salvas que serão perdidas se sair sem salvar.
              <div className="mt-2 p-2 bg-gray-100 rounded text-sm">
                <strong>Último salvamento:</strong> {formatLastSavedTime(lastSavedTime)}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelNavigation}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                await handleSave();
                confirmNavigation();
              }}
              className="bg-voah-teal hover:bg-voah-teal/90"
            >
              Salvar e sair
            </AlertDialogAction>
            <AlertDialogAction 
              onClick={confirmNavigation}
              className="bg-red-500 hover:bg-red-600"
            >
              Sair sem salvar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
} 